import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  sortBy,
  find,
} from 'lodash';
import {
  makeStyles,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Select,
  MenuItem,
  Typography,
  Grid,
  Card,
  CardContent,
  ListItemSecondaryAction,
  Checkbox,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import FieldDisplayMessage from '../../../../../../widgets/FieldDisplayMessage';
import StatusBadge from '../../../../../../widgets/StatusBadge';
import StatusBadgeList from '../../../../../../widgets/StatusBadgeList';

import StatusCommunicationModal from './components/StatusCommunicationModal';
import WorkflowStatus from './components/WorkflowStatus';
import styles from './styles';

const useStyles = makeStyles(styles);

export const StatusesFormComponent = (props) => {
  const classes = useStyles();
  const {
    eventTemplate,
    statuses,
    onDefaultStartStatusChange,
    onChangeWorkflowStatuses,
    isLoading,
  } = props;

  const [openStatusCommunicationModal, setOpenStatusCommunicationModal] = useState(false);
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState({});

  if (eventTemplate.defaultStartStatus && eventTemplate.defaultStartStatus.id && eventTemplate.workflowStatuses.length === 0) {
    onChangeWorkflowStatuses([{
      statusId: eventTemplate.defaultStartStatus.id,
      order: 0,
    }]);
  }

  const getDefaultStartingStatus = (workflowStatuses = eventTemplate.workflowStatuses) => find(statuses, (status) => status.id === (sortBy(workflowStatuses, 'order', 'name')[0] || {}).statusId);

  const getOtherWorkflowStatuses
    = (excludedWorkflows) => eventTemplate.workflowStatuses.filter(
      (status) => excludedWorkflows.map(
        (excludedWorkflow) => excludedWorkflow.statusId,
      ).indexOf(status.statusId) === -1,
    );

  const handleClickStatusCheckbox = (status) => {
    const doesStatusAlreadyExist = eventTemplate.workflowStatuses.filter((workflowStatus) => workflowStatus.statusId === status.id);
    const newWorkflowStatuses = doesStatusAlreadyExist.length > 0
      ? eventTemplate.workflowStatuses.filter((workflowStatus) => workflowStatus.statusId !== status.id)
      : [
        ...eventTemplate.workflowStatuses,
        {
          statusId: status.id,
          order: eventTemplate.workflowStatuses.length,
        },
      ];
    onChangeWorkflowStatuses(newWorkflowStatuses);
    onDefaultStartStatusChange(getDefaultStartingStatus(newWorkflowStatuses));
  };

  const handleChangeAssignmentRequiredFlag = (workflowStatus) => {
    const otherWorkflowStatuses = getOtherWorkflowStatuses([workflowStatus]);
    const workflowStatuses = [...otherWorkflowStatuses, {
      ...workflowStatus,
      requiredAssignment: !workflowStatus.requiredAssignment,
    }];
    onChangeWorkflowStatuses(workflowStatuses);
    onDefaultStartStatusChange(getDefaultStartingStatus(workflowStatuses));
  };

  const handleNextStatusOptionsChange = (workflowStatus, statusId) => {
    const otherWorkflowStatuses = getOtherWorkflowStatuses([workflowStatus]);
    const isAddingStatus = !workflowStatus.nextStatusOptions
      || workflowStatus.nextStatusOptions.filter((id) => id === statusId).length === 0;

    const workflowStatuses = [...otherWorkflowStatuses, {
      ...workflowStatus,
      nextStatusOptions: isAddingStatus
        ? [...(workflowStatus.nextStatusOptions || []), statusId]
        : workflowStatus.nextStatusOptions.filter((id) => id !== statusId),
    }];
    onChangeWorkflowStatuses(workflowStatuses);
    onDefaultStartStatusChange(getDefaultStartingStatus(workflowStatuses));
  };

  const handleChangeStatusCommunication = (updatedWorkflowStatus) => {
    const otherWorkflowStatuses = getOtherWorkflowStatuses([updatedWorkflowStatus]);
    const workflowStatuses = [...otherWorkflowStatuses, updatedWorkflowStatus];
    onChangeWorkflowStatuses(workflowStatuses);
    onDefaultStartStatusChange(getDefaultStartingStatus(workflowStatuses));
  };

  const isExternalEmailCommunicationSent = (workflowStatus) => (
    !!workflowStatus
      && !!workflowStatus.statusCommunication
      && !!workflowStatus.statusCommunication.communicateToEventTemplateId
  );

  const showStatusCommunicationModal = (workflowStatus) => {
    setSelectedWorkflowStatus(workflowStatus);
    setOpenStatusCommunicationModal(true);
  };

  const handleMoveWorkflowStatusOrderChange = (updatedWorkflowStatus, adjecentWorkflowStatus) => {
    const otherWorkflowStatuses = getOtherWorkflowStatuses([updatedWorkflowStatus, adjecentWorkflowStatus]);
    const workflowStatuses = [
      ...otherWorkflowStatuses,
      {
        ...updatedWorkflowStatus,
        order: adjecentWorkflowStatus.order,
      },
      {
        ...adjecentWorkflowStatus,
        order: updatedWorkflowStatus.order,
      },
    ];
    onChangeWorkflowStatuses(workflowStatuses);
    onDefaultStartStatusChange(getDefaultStartingStatus(workflowStatuses));
  };

  const handleDeleteWorkflowStatus = (updatedWorkflowStatus) => {
    const otherWorkflowStatuses = getOtherWorkflowStatuses([updatedWorkflowStatus]);
    onChangeWorkflowStatuses(otherWorkflowStatuses);
    onDefaultStartStatusChange(getDefaultStartingStatus(otherWorkflowStatuses));
  };

  const sortWorkflowStatusesBy = ['order', 'name', 'statusId'];
  const sortedWorkflowStatuses = sortBy(eventTemplate.workflowStatuses, sortWorkflowStatusesBy);

  const label = (
    <Grid item xs={12} md={3} lg={2}>
      <Typography variant="subtitle2">Statuses</Typography>
      <Typography variant="caption" gutterBottom>
        Specify which statuses are available in the workflow.
      </Typography>
      <Typography variant="caption" gutterBottom>
        Also set behavior for the workflow when it reaches said status.
      </Typography>
    </Grid>
  );

  if (isLoading) {
    return (
      <Grid container spacing={4}>
        {label}
        <Grid item xs={12} md={9} lg={10} container className={classes.section}>
          <Card className={classes.card}>
            <CardContent>
              <div className={classes.statusSkeletonContainer}>
                <Skeleton variant="circle" width={40} height={40} className={classes.circleSkeleton} />
                <Skeleton variant="text" className={classes.flexSkeleton} />
              </div>
              <div className={classes.statusSkeletonContainer}>
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="text" className={classes.flexSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
              </div>
              <div className={classes.statusSkeletonContainer}>
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="text" className={classes.flexSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
              </div>
              <div className={classes.statusSkeletonContainer}>
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="text" className={classes.flexSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
              </div>
              <div className={classes.statusSkeletonContainer}>
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="text" className={classes.flexSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
                <Skeleton variant="rect" width={20} height={20} className={classes.squareSkeleton} />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={4}>
      {label}
      <Grid item xs={12} md={9} lg={10} container className={classes.section}>
        <Card className={classes.card}>
          <CardContent>
            <FormControl
              style={{ marginTop: 16 }}
              fullWidth
              required
              variant="outlined"
            >
              {
                statuses.length > 0
                  ? (
                    <div>
                      <InputLabel htmlFor="status-select">Workflow Statuses</InputLabel>
                      <Select
                        style={{ width: '100%' }}
                        input={(
                          <OutlinedInput
                            labelWidth={160}
                            name="status"
                            id="status-select"
                            classes={{ input: classes.selectInput }}
                          />
                        )}
                        value={eventTemplate.workflowStatuses}
                        multiple
                        renderValue={() => (
                          <StatusBadgeList
                            statuses={statuses}
                            displayedStatuses={eventTemplate.workflowStatuses}
                            statusIdField="statusId"
                            maxLength={6}
                            sortBy={sortWorkflowStatusesBy}
                            emptyListLabel="No statuses selected"
                          />
                        )}
                      >
                        {
                          statuses
                            .sort((a, b) => a.statusOrder - b.statusOrder).map((status) => (
                              <MenuItem key={`status-${status.id}`} value={status}>
                                <StatusBadge
                                  icon={status.icon}
                                  iconColor={status.iconColor}
                                  label={status.name}
                                />
                                <ListItemSecondaryAction>
                                  <Checkbox
                                    checked={!!find(
                                      eventTemplate.workflowStatuses,
                                      (workflowStatus) => workflowStatus.statusId === status.id,
                                    )}
                                    onClick={() => handleClickStatusCheckbox(status)}
                                  />
                                </ListItemSecondaryAction>
                              </MenuItem>
                            ))
                        }
                      </Select>
                    </div>
                  )
                  : (
                    <FieldDisplayMessage
                      title="No Statuses defined for your organization"
                      subTitle="Admins can create statuses from the Event Settings page"
                      icon="info_outlined"
                      disableGutters
                    />
                  )
              }
              <FormHelperText>
                You must select at least one status to act as a starting status.
                This is the status the workflow will begin in.
              </FormHelperText>
            </FormControl>
          </CardContent>
        </Card>
        {
          sortedWorkflowStatuses
            .map((workflowStatus, index, mappedArray) => {
              const nextWorkflowStatus = mappedArray[index + 1];
              const previousWorkflowStatus = mappedArray[index - 1];
              return (
                <WorkflowStatus
                  key={`status-check-accordian-${workflowStatus.statusId}`}
                  workflowStatus={workflowStatus}
                  workflowStatuses={sortedWorkflowStatuses}
                  statuses={statuses}
                  index={index}
                  nextWorkflowStatus={nextWorkflowStatus}
                  previousWorkflowStatus={previousWorkflowStatus}
                  onMoveBack={handleMoveWorkflowStatusOrderChange}
                  onMoveForward={handleMoveWorkflowStatusOrderChange}
                  onDelete={handleDeleteWorkflowStatus}
                  onChangeAssignmentRequiredFlag={handleChangeAssignmentRequiredFlag}
                  isExternalEmailCommunicationSent={isExternalEmailCommunicationSent}
                  onChangeStatusCommunication={handleChangeStatusCommunication}
                  showStatusCommunicationModal={showStatusCommunicationModal}
                  onNextStatusOptionsChange={handleNextStatusOptionsChange}
                />
              );
            })
        }
        <StatusCommunicationModal
          isOpen={openStatusCommunicationModal}
          eventTemplate={eventTemplate}
          workflowStatus={selectedWorkflowStatus}
          handleUpdate={handleChangeStatusCommunication}
          onClose={() => { setOpenStatusCommunicationModal(false); }}
        />
      </Grid>
    </Grid>
  );
};

StatusesFormComponent.propTypes = {
  eventTemplate: PropTypes.object,
  statuses: PropTypes.array,
  onDefaultStartStatusChange: PropTypes.func,
  onChangeWorkflowStatuses: PropTypes.func,
  isLoading: PropTypes.bool,
};

StatusesFormComponent.defaultProps = {
  eventTemplate: {},
  statuses: [],
  onDefaultStartStatusChange: () => {},
  onChangeWorkflowStatuses: () => {},
  isLoading: true,
};

export default StatusesFormComponent;
